'use client';

import { Grid, Heading, Legend, TextPassage } from '@petsmart-ui/sparky';
import { NEW_TAB, TARGET_BLANK, TARGET_SELF } from '@libs/contentful';
import type {
    BenefitsItem,
    LinkAction,
    PRIMARY_BUTTON_STYLE,
    SECONDARY_BUTTON_STYLE,
    LoyaltyBenefitsItem,
} from '@libs/contentful';
import { useEffect, useMemo, useState } from 'react';
import { useCart, useDecisions, useOptimizely } from '@libs/common-next';
import { DeliveryType, NAVIGATION_URLS, getAccessTokenSoft } from '@libs/core';
import type { SignInFormProps } from '../SignInForm';
import { SignInForm } from '../SignInForm';
import { BenefitItems } from '../BenefitItems';
import type { CTAWithLinkEntry } from './utils';
import { getButtonVariant } from './utils';
import { GenericButton } from '../GenericButton';

import './InterstitialSignIn.scss';
import type { TreatsLoginLocation } from '@libs/analytics';
import { SignInFormSoft } from '../SignInFormSoft';
import { usePathname } from 'next/navigation';

export type InterstitialCta = {
    fields: CTAWithLinkEntry;
};

export type InterstitialLinkAction = {
    fields: LinkAction;
};

type InterstitialSignInProps = SignInFormProps & {
    title?: string;
    subtext?: string;
    benefitItems?: BenefitsItem[] | LoyaltyBenefitsItem[];
    createAccountCta?: InterstitialCta;
    itemsTitle?: string;
    createAccountHref?: string;
    displayAutoshipDescription?: boolean;
    location?: TreatsLoginLocation;
    pathOverride?: string;
    customRedirectUrl?: string | null;
};

const componentClassName = 'interstitial-sign-in';

export function InterstitialSignIn({
    title,
    subtext,
    benefitItems,
    createAccountCta,
    itemsTitle,
    cta,
    linkAction,
    isInDrawer,
    createAccountHref,
    onSuccessfulSignIn,
    displayAutoshipDescription,
    location,
    pathOverride,
    customRedirectUrl,
}: Readonly<InterstitialSignInProps>) {
    const [isCheckoutPage, setIsCheckoutPage] = useState(false);

    const accessTokenSoft = getAccessTokenSoft();

    const { cart } = useCart();
    const currentPage = usePathname();
    const { context } = useOptimizely();
    const { exp_loyalty_off: isLoyaltyExpOff, soft_login } = useDecisions();

    const isSoftLoginEnabled = soft_login && !!accessTokenSoft;

    useEffect(() => {
        if (currentPage) {
            setIsCheckoutPage(['/checkout-login', '/cart'].includes(currentPage));
        }
    }, [currentPage]);

    const isAutoship = useMemo(() => {
        if (!cart?.fulfillmentGroups || !displayAutoshipDescription) {
            return false;
        }
        return (
            cart.fulfillmentGroups.length > 0 &&
            cart.fulfillmentGroups.find(group => group.fulfillmentMethod.toLowerCase() === DeliveryType.AUTOSHIP) !==
                undefined
        );
    }, [cart?.fulfillmentGroups, displayAutoshipDescription]);

    const createAccountVariant: typeof PRIMARY_BUTTON_STYLE | typeof SECONDARY_BUTTON_STYLE = createAccountCta
        ? getButtonVariant(createAccountCta)
        : 'table-header';

    const createAccountLink = isCheckoutPage
        ? `${NAVIGATION_URLS.velocity.createAccount}?redirectUrl=/checkout`
        : (createAccountHref ?? createAccountCta?.fields?.link?.fields?.webUrl ?? '/create-account');

    return (
        <div data-testid="interstitial-sign-in" className={componentClassName}>
            {isAutoship && (
                <TextPassage capLinelength={false} style={{ marginBottom: '24px' }}>
                    All autoship items require an account to checkout. Please sign in or create an account to continue.
                </TextPassage>
            )}

            {(title || subtext) && (
                <Grid.Item className={`${componentClassName}__headings`}>
                    {title && (
                        <Heading tagName="h5" className={`${componentClassName}__heading`}>
                            {title}
                        </Heading>
                    )}
                    {subtext && (
                        <Legend
                            className={`${componentClassName}__sub-heading`}
                            legendSize="sm"
                            required
                            text={subtext}
                        />
                    )}
                </Grid.Item>
            )}

            {isSoftLoginEnabled ? (
                <SignInFormSoft
                    {...{
                        cta,
                        linkAction,
                        isInDrawer,
                    }}
                />
            ) : (
                <>
                    <SignInForm
                        {...{
                            cta,
                            linkAction,
                            isAutoship,
                            isInDrawer,
                            onSuccessfulSignIn,
                            location,
                            pathOverride,
                            customRedirectUrl,
                        }}
                    />
                    <div
                        className={
                            isInDrawer
                                ? `${componentClassName}__benefits-container__drawer`
                                : `${componentClassName}__benefits-container`
                        }
                    >
                        {itemsTitle && (
                            <Heading size="body-lg-bold" tagName="h2">
                                {itemsTitle}
                            </Heading>
                        )}

                        {benefitItems && <BenefitItems data-testid="benefit-items" items={benefitItems} />}
                    </div>
                    {benefitItems && (
                        <GenericButton
                            variant={createAccountVariant}
                            text={createAccountCta?.fields?.text || 'Create account'}
                            size="lg"
                            target={createAccountCta?.fields?.behavior === NEW_TAB ? TARGET_BLANK : TARGET_SELF}
                            onClick={() => {
                                // TODO: [AC-4466] change to href={createAccountLink} once test is over
                                context?.trackEvent('create_account');
                                if (isLoyaltyExpOff && window.localStorage.getItem('opt-ex-offer-id')) {
                                    context?.trackEvent('fly_out_create_account');
                                }
                                window.location.href = createAccountLink;
                            }}
                            rel="noreferrer"
                            data-testid="create-account-btn"
                            fullWidth
                        />
                    )}
                </>
            )}
        </div>
    );
}
